import { useCallback } from "react";
import { Megaphone } from "lucide-react";
import { getCampaignImpact } from "@/api/client";
import type {
  CampaignImpactCampaign,
  CampaignImpactStoreRow,
  CampaignImpactStoreUpcRow,
} from "@/api/types";
import { BudgetMeter } from "@/components/BudgetMeter";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { TwoSeriesTimeChart } from "@/components/TwoSeriesTimeChart";
import { HorizontalBarChart } from "@/components/HorizontalBarChart";
import { MeterCard } from "@/components/MeterCard";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { StatCard } from "@/components/StatCard";
import { TimeSeriesAreaChart } from "@/components/TimeSeriesAreaChart";
import { UpcListInput } from "@/components/UpcListInput";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import {
  formatCompact,
  formatCurrency,
  formatCurrencyCompact,
  formatDate,
  formatDelta,
  formatNumber,
  formatPercent,
} from "@/lib/format";

const campaignColumns: Column<CampaignImpactCampaign>[] = [
  {
    header: "Campaign",
    cell: (r) => r.campaignName,
    sortValue: (r) => r.campaignName,
  },
  {
    header: "Type",
    cell: (r) => r.campaignType,
    sortValue: (r) => r.campaignType,
  },
  {
    header: "Runs",
    cell: (r) => `${formatDate(r.beginDate)} – ${formatDate(r.endDate)}`,
    // ISO dates sort correctly as strings.
    sortValue: (r) => r.beginDate,
    csvValue: (r) => `${r.beginDate} to ${r.endDate}`,
  },
  {
    header: "Reward",
    align: "right",
    cell: (r) => `$${r.rewardValue.toFixed(2)}`,
    sortValue: (r) => r.rewardValue,
  },
  {
    header: "Redemptions",
    align: "right",
    cell: (r) => formatNumber(r.redemptions),
    sortValue: (r) => r.redemptions,
  },
  {
    header: "Spend",
    align: "right",
    cell: (r) => formatCurrency(r.spend),
    sortValue: (r) => r.spend,
  },
  {
    header: "Budget used",
    cell: (r) => <BudgetMeter spend={r.spend} budget={r.budget} />,
    // Sort by fraction of budget burned; campaigns without a budget last.
    sortValue: (r) => (r.budget ? r.spend / r.budget : null),
    // The meter doesn't translate to CSV; export the raw budget instead.
    csvValue: (r) => r.budget,
    csvHeader: "Budget",
  },
];

const storeColumns: Column<CampaignImpactStoreRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  {
    header: "Redemptions",
    align: "right",
    cell: (r) => formatNumber(r.redemptions),
    sortValue: (r) => r.redemptions,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

const storeUpcColumns: Column<CampaignImpactStoreUpcRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Lowest price",
    align: "right",
    cell: (r) => formatCurrency(r.minPrice),
    sortValue: (r) => r.minPrice,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

export function CampaignImpactPage() {
  const filters = useProductFilterOptions();
  const [campaigns, setCampaigns, campaignId] = useUrlListFilter("campaignId");
  const [upcsDraft, setUpcs, upcs] = useDebouncedUrlFilter("upcs");

  const fetchImpact = useCallback(
    (token: string) =>
      getCampaignImpact(token, { campaignId, upcs: upcs || undefined }),
    [campaignId, upcs],
  );
  const state = useApiData(fetchImpact);
  // null = no campaigns selected yet (the endpoint requires a list)
  const data = state.status === "ready" ? state.data : null;
  // Units cover only the UPCs in the filter; without them the endpoint skips
  // the units queries and the units widgets prompt for input instead.
  const hasUpcs = Boolean(upcs.trim());
  // liftStart (campaign begin or first redemption, whichever came first)
  // drives both the chart marker and the API's lift averages, so the line
  // and the numbers always agree.
  const markerLabel =
    data && data.liftStart !== data.campaignStart
      ? "First redemption"
      : "Campaign start";
  // Avg-daily-units lift vs the pre-campaign baseline; null when there is
  // no baseline to compare against (no UPCs, or zero baseline sales).
  const unitsLift =
    data && hasUpcs && data.averageDailyUnits.baseline > 0
      ? data.averageDailyUnits.campaign / data.averageDailyUnits.baseline - 1
      : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Campaign impact</h1>
        <p className="text-sm text-muted-foreground">
          Redemptions, spend, and unit movement for the selected campaigns
          over their own run dates
          {data
            ? ` — ${formatDate(data.campaignStart)} to ${formatDate(data.windowEnd)}`
            : ""}
          .
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <MultiFilterSelect
          label="Campaign"
          allLabel="Select campaigns"
          values={campaigns}
          onChange={setCampaigns}
          disabled={filters.disabled}
          options={filters.campaignOptions}
        />
        <UpcListInput
          value={upcsDraft}
          onApply={setUpcs}
          label="UPCs (units track only these)"
        />
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : state.status === "ready" && data === null ? (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-6 py-14 text-center">
          <div
            className="mb-1 flex h-11 w-11 items-center justify-center rounded-full"
            style={{ background: "var(--viz-meter-track)" }}
          >
            <Megaphone
              className="h-5 w-5"
              style={{ color: "var(--viz-series-1)" }}
              aria-hidden
            />
          </div>
          <p className="text-sm font-medium">No campaigns selected</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Pick one or more campaigns above to see redemptions, budget burn,
            and how their products moved by store.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Redemptions"
              value={data && formatCompact(data.totals.redemptions)}
            />
            <StatCard
              label="Identified shoppers"
              value={data && formatCompact(data.totals.uniqueShoppers)}
            />
            <StatCard
              label="Units moved"
              value={data && (hasUpcs ? formatCompact(data.totals.units) : "—")}
            />
            <StatCard
              label="Stores reached"
              value={
                data &&
                formatCompact(
                  data.byStore.filter((s) => s.redemptions > 0).length,
                )
              }
            />
            <StatCard
              label="Campaign budget"
              value={
                data &&
                (data.totals.budget === null
                  ? "—"
                  : formatCurrencyCompact(data.totals.budget))
              }
            />
            <StatCard
              label="Campaign spend"
              value={data && formatCurrency(data.totals.spend)}
            />
            <StatCard
              label="Campaign lift"
              value={
                data &&
                (unitsLift !== null
                  ? `${unitsLift >= 0 ? "+" : ""}${formatPercent(unitsLift)}`
                  : "—")
              }
            />
            <MeterCard
              label="Budget used"
              value={
                data
                  ? data.totals.budget
                    ? data.totals.spend / data.totals.budget
                    : null
                  : undefined
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Campaign lift</CardTitle>
              <CardDescription>
                {!hasUpcs
                  ? "Daily redemptions — add UPCs to the filter to populate the units series"
                  : data
                    ? `Daily redemptions alongside units for the filtered UPCs, including a ${data.baselineDays}-day pre-campaign baseline (campaigns start ${formatDate(data.campaignStart)})`
                    : "Daily redemptions alongside units for the filtered UPCs, including a pre-campaign baseline"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data && hasUpcs ? (
                <div className="mb-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <div>
                    <div className="text-muted-foreground">
                      Avg daily units, baseline
                    </div>
                    <div className="font-medium">
                      {formatNumber(data.averageDailyUnits.baseline)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">
                      Avg daily units, campaign
                    </div>
                    <div className="font-medium">
                      {formatNumber(data.averageDailyUnits.campaign)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Lift</div>
                    <div className="font-medium">
                      {unitsLift !== null
                        ? formatDelta(unitsLift, "baseline")
                        : "—"}
                    </div>
                  </div>
                </div>
              ) : null}
              {!data ? (
                <ChartSkeleton height={300} />
              ) : (
                <TwoSeriesTimeChart
                  data={data.trend.map((d) => ({
                    day: d.day,
                    a: d.redemptions,
                    b: d.units,
                  }))}
                  aName="Redemptions"
                  bName="Units"
                  height={300}
                  markerDay={data.liftStart}
                  markerLabel={markerLabel}
                />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Spend pacing</CardTitle>
                <CardDescription>Daily redemption payouts</CardDescription>
              </CardHeader>
              <CardContent>
                {!data ? (
                  <ChartSkeleton height={260} />
                ) : (
                  <TimeSeriesAreaChart
                    data={data.spendByDay.map((d) => ({
                      day: d.day,
                      value: d.spend,
                    }))}
                    seriesName="Spend"
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Redemptions by DMA</CardTitle>
                <CardDescription>Store redemptions by DMA</CardDescription>
              </CardHeader>
              <CardContent>
                {data ? (
                  <HorizontalBarChart
                    data={data.redemptionsByDma.map((r) => ({
                      name: r.dma,
                      value: r.redemptions,
                    }))}
                    seriesName="Redemptions"
                  />
                ) : (
                  <ChartSkeleton height={260} />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Campaigns</CardTitle>
                <CardDescription>
                  Each selected campaign with redemption counts and budget burn
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="campaign-impact-campaigns"
                  columns={campaignColumns}
                  rows={data.campaigns}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={campaignColumns}
                  rows={data.campaigns}
                  rowKey={(r) => r.campaignId}
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>Stores</CardTitle>
                  <CardDescription>
                    Redemptions per store; units cover only the UPCs in the
                    filter
                  </CardDescription>
                </div>
                {data ? (
                  <CsvDownloadButton
                    name="campaign-impact-by-store"
                    columns={storeColumns}
                    rows={data.byStore}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={storeColumns}
                    rows={data.byStore}
                    rowKey={(r) => r.storeId}
                    maxHeight="28rem"
                    emptyMessage="No redemptions or sales in range"
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>UPCs by store</CardTitle>
                  <CardDescription>
                    Lowest observed price per store per filtered UPC during the
                    campaign
                  </CardDescription>
                </div>
                {data ? (
                  <CsvDownloadButton
                    name="campaign-impact-by-store-and-upc"
                    columns={storeUpcColumns}
                    rows={data.byStoreUpc}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={storeUpcColumns}
                    rows={data.byStoreUpc}
                    rowKey={(r) => `${r.storeId}-${r.upc}`}
                    maxHeight="28rem"
                    emptyMessage={
                      hasUpcs
                        ? "No sales in range"
                        : "Add UPCs to the filter to see units"
                    }
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
